// Firebase Cloud Messaging — frontend lib.
//
// Owns: permission prompt, getToken(), onMessage foreground handler,
// register/unregister with backend.
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { firebaseApp } from './firebase';
import { axiosInstance } from '../config/axios';

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

let _messaging = null;
let _supported = null;

async function ensureSupported() {
  if (_supported !== null) return _supported;
  try {
    _supported = await isSupported();
  } catch {
    _supported = false;
  }
  return _supported;
}

async function getMessagingInstance() {
  if (_messaging) return _messaging;
  if (!(await ensureSupported())) return null;
  _messaging = getMessaging(firebaseApp);
  return _messaging;
}

/** Returns 'granted' | 'denied' | 'default' | 'unsupported'. */
export async function pushPermissionStatus() {
  if (!(await ensureSupported())) return 'unsupported';
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

/** Request permission (idempotent — Notification.requestPermission caches). */
export async function requestPushPermission() {
  if (!(await ensureSupported())) return 'unsupported';
  if (typeof Notification === 'undefined') return 'unsupported';
  return await Notification.requestPermission();
}

/** Register the service worker and obtain an FCM token. */
async function _getFcmToken() {
  const messaging = await getMessagingInstance();
  if (!messaging) return null;
  if (!VAPID_KEY) {
    console.warn('REACT_APP_FIREBASE_VAPID_KEY missing — push disabled');
    return null;
  }

  // Register the SW explicitly so we control the path/scope.
  let swReg = null;
  if ('serviceWorker' in navigator) {
    swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  }

  return await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: swReg || undefined,
  });
}

/**
 * Run the full enable flow: prompt for permission (if needed), get FCM token,
 * register it on the backend. Returns:
 *   { ok: bool, status: 'granted'|'denied'|'unsupported'|'error', token?: str }
 */
export async function enablePush() {
  if (!(await ensureSupported())) return { ok: false, status: 'unsupported' };

  let perm = await pushPermissionStatus();
  if (perm === 'default') {
    perm = await requestPushPermission();
  }
  if (perm !== 'granted') return { ok: false, status: perm };

  try {
    const token = await _getFcmToken();
    if (!token) return { ok: false, status: 'error' };

    await axiosInstance.post('/notifications/push/tokens', {
      token,
      platform: 'web',
      user_agent: navigator.userAgent || null,
    });
    // Remember locally so we don't pester the user again.
    localStorage.setItem('byard-push-enabled', '1');
    return { ok: true, status: 'granted', token };
  } catch (e) {
    console.error('enablePush failed:', e);
    return { ok: false, status: 'error' };
  }
}

/** Unregister the local FCM token from the backend. Best-effort. */
export async function disablePush() {
  try {
    const messaging = await getMessagingInstance();
    if (!messaging || !VAPID_KEY) return;
    const token = await getToken(messaging, { vapidKey: VAPID_KEY });
    if (token) {
      await axiosInstance.delete(
        `/notifications/push/tokens/${encodeURIComponent(token)}`
      );
    }
  } catch (e) {
    // Non-fatal — backend has its own dead-token pruning.
  } finally {
    localStorage.removeItem('byard-push-enabled');
  }
}

/** Foreground message handler. Returns the unsubscribe fn. */
export async function subscribeForeground(handler) {
  const messaging = await getMessagingInstance();
  if (!messaging) return () => {};
  return onMessage(messaging, handler);
}
